import { ProductImageModelType } from '@teinor/erp/types/company/product/productImage'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import ProductEditImage from './ProductEditImage'
import { EditImageTy } from './type'
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { faArrowsUpDownLeftRight } from '@fortawesome/pro-light-svg-icons';
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types';
import TwinIcon from '../../../../../../baseComponents/TwinIcon';
import TwinTrans from '../../../../../../baseComponents/TwinTrans';
import FormRenderer from '../../../../../../forms/FormRenderer';
import { FormRender } from '../../../../../../forms/FormRenderer/types';
import { twinFetchPostJSON, twinFetchPostText, uploadFileToBack } from '../../../../../../utils/globals/data';


interface ProductImagesProps extends ComponentWithPermissions {
    ProductId: string
}

const ProductImages: React.FC<ProductImagesProps> = ({ userPermissions, ProductId }) => {
    const { setEditImage, editImage, images, getImages, sensors, handleDragStart, handleDragEnd } = useProductImagesLogic({ ProductId })

    return (
        <div className='mb-36'>
            <span className='regular18 mb-23 block'>
                <TwinTrans transKey={'images'}>Imágenes</TwinTrans>
            </span>
            <div className='product_images_container flex relative'>
                <div className='flex flex-wrap w-full'>
                    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
                        <RenderProductImages {...{ images, getImages, editImage, setEditImage, ProductId, userPermissions }} />
                    </DndContext>
                </div>
                {editImage && <ProductEditImage  {...{ editImage, getImages, setEditImage, userPermissions }} />}
            </div>
        </div>
    )
}
interface ProductImagesLogicProps {
    ProductId: string
}
const useProductImagesLogic = ({ ProductId }: ProductImagesLogicProps) => {
    const [editImage, setEditImage] = useState<EditImageTy>(null)
    const [images, setImages] = useState<ProductImageModelType[]>([])

    const getImages = useCallback(async () => {
        if (ProductId) {
            const data = await twinFetchPostJSON('/api/app/product/productImage/getAllProductImages', { ProductId })
            setImages(data)
        }
    }, [setImages, ProductId])

    useEffect(() => {
        getImages()
    }, [getImages])

    const handleDragStart = ((event: DragEndEvent) => {
        const { active } = event
        const element = document.getElementsByClassName('product_image_container')
        const position = active?.data?.current?.sortable.index + 1
        element[position].classList.add("image_sortable")
    })

    const handleDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        if (active.id !== over?.id) {
            let oldIndex = active?.data?.current?.sortable.index + 1
            let newIndex = over?.data?.current?.sortable.index + 1
            setImages((oldItems: any) => {
                if (oldIndex !== 0 && newIndex !== 0) {
                    const items: ProductImageModelType[] = arrayMove(oldItems, oldIndex, newIndex)
                    for (const key in items) {
                        items[key].position = parseInt(key) + 1
                    }
                    return items
                }
                return [...oldItems]
            })
            const element = document.getElementsByClassName('image_sortable')
            element[0].classList.remove('image_sortable')
            await twinFetchPostText('/api/app/product/productImage/updateProductImageOrder', { productImages: images })
        }
    }, [setImages, images])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 75,
                tolerance: 5,
            },
        })
    )
    return { setEditImage, editImage, images, getImages, handleDragStart, handleDragEnd, sensors }
}


interface RenderProductImagesProps extends ProductImagesProps {
    images: ProductImageModelType[]
    setEditImage: Dispatch<SetStateAction<EditImageTy>>
    editImage: EditImageTy
    getImages: () => Promise<void>
}

const RenderProductImages: React.FC<RenderProductImagesProps> = ({ images, setEditImage, editImage, ProductId, userPermissions, getImages }) => {
    const ids: number[] = []
    const renderImages: JSX.Element[] = []
    for (const key in images) {
        const image = images[key]
        const { id } = image
        if (parseInt(key) !== 0) {
            ids.push(id)
            renderImages.push(<ProductImg key={id} className={(editImage?.id === id ? 'image_active ' : '')} {...{ id, image, setEditImage }} />)
        }
    }
    return (
        <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className='container_product_images'>
                <UploaderImage id={ProductId} {...{ userPermissions, getImages }} />
                {images.length > 0 && <ProductCover id={images[0].id} image={images[0]} setEditImage={setEditImage} className={editImage?.id === images[0].id ? 'image_active' : ''} />}
                {images.length > 0 && renderImages}
            </div>
        </SortableContext>
    )
}

interface UploaderImageProps extends UploaderImageLogicProps { }

const UploaderImage: React.FC<UploaderImageProps> = ({ id, getImages }) => {
    const { handleCreateImage } = useUploaderImageLogic({ id, getImages })
    const fields: FormRender<ProductImageModelType> = [
        {
            cols: 1,
            elements: [
                {
                    label: '',
                    name: 'url',
                    component: 'ImageInput',
                    onChange: (file) => handleCreateImage(file),
                    emptyAfterChange: true
                },
            ]
        }
    ]
    return (
        <FormRenderer sections={fields} />
    )
}

interface UploaderImageLogicProps {
    id: string
    getImages: () => Promise<void>
}

const useUploaderImageLogic = ({ id, getImages }: UploaderImageLogicProps) => {
    const handleCreateImage = useCallback(async (files: any) => {
        let result: any = false
        for (const file of files) {
            const url = await uploadFileToBack(file, file.name, ['product', id])
            result = await twinFetchPostText('/api/app/product/productImage/createProductImage', { url: url, ProductId: id })
        }
        if (result) {
            getImages()
        }
    }, [getImages, id])

    return { handleCreateImage }
}

interface ProductCoverProps extends ProductImgProps { }

const ProductCover: React.FC<ProductCoverProps> = ({ ...all }) => {
    return (
        <ProductImg {...all}>
            <div className='relative'>
                <div className='absolute main_product_image '>
                    <TwinTrans transKey='cover'>
                        Portada
                    </TwinTrans>
                </div>
            </div>
        </ProductImg>
    )
}

interface ProductImgProps {
    id: number
    image: ProductImageModelType
    setEditImage: (data: EditImageTy) => void
    className?: string
    children?: React.ReactNode
}

const ProductImg: React.FC<ProductImgProps> = ({ id, image, setEditImage, className, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
    const style = { transform: CSS.Transform.toString(transform), transition }

    return (
        <div className={'product_image_container ' + (image.is_main ? 'product_image_isMain ' : '') + (className || '')} ref={setNodeRef} onClick={() => setEditImage(image)} style={style} >
            {children}
            <div className='absolute flex product_image_moved'>
                <div className='move_icon cursor-pointer px-10 py-5' {...attributes} {...listeners} >
                    <TwinIcon icon={faArrowsUpDownLeftRight} />
                </div>
            </div>
            <img src={image?.url} className='product_image' alt={''} />
        </div>)
}

export default ProductImages